// ─── SectionV1CollageCanvas ───────────────────────────────────
// Public-only renderer for the section-v1 layout model.
//
// Differences from the legacy CollageCanvas:
//   • Section is sized by aspect-ratio (width: 100%; height computed) so that
//     percentage Y positions remain visually proportional across viewport
//     widths (no more drift between Studio preview at ~390px and a real phone
//     at 360–430px).
//   • Element positioning uses xPercent / yPercent / widthPercent / anchor.
//     Anchor defaults to 'c' (center) so legacy data renders identically.
//   • No edit affordances at all — this is for the public page only.
//
// Phase 1 keeps both legacy and new fields in the data. We read the new ones
// when present and fall back to legacy x/y/w transparently.

import React, { useState, useEffect, useContext } from 'react';
import CollageElementRenderer from '../collage/CollageElementRenderer';
import { getRenderableElements } from '../../lib/utils/collageIntent';
import {
  getElementV1Style,
  pickV1LayoutForViewport,
  resolveSectionLayout,
  resolveElementBounds,
} from '../../lib/utils/sectionLayout';
import ScrollEntrance from '../collage/ScrollEntrance';
import { getElementMotionRole, softPresetForRole } from '../../lib/utils/getElementMotionRole';
import { getRevealGroup, delayForGroup } from '../../lib/utils/getRevealGroup';
import { CollageEditContext } from '../collage/CollageEditContext';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

function BackgroundLayer({ background, globalBgActive }) {
  if (!background || globalBgActive) return null;
  if (background.type === 'color') {
    return <div className="absolute inset-0" style={{ background: background.color }} />;
  }
  if (background.type === 'image') {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `${
              background.overlay
                ? `linear-gradient(${background.overlay}, ${background.overlay}), `
                : ''
            }url(${background.src})`,
            filter: `grayscale(${background.grayscale || 0})`,
            transform: 'scale(1.03)',
          }}
        />
      </div>
    );
  }
  return null;
}

// ─── Inline widget renderer (mirrors legacy) ──────────────────
function InlineWidgetRenderer({ section, data, theme, settings, rsvpRef }) {
  const [Component, setComponent] = useState(null);
  useEffect(() => {
    const registry = {
      timeline: () => import('../sections/timeline/TimelineSection'),
      gallery: () => import('../sections/gallery/GallerySection'),
      rsvp: () => import('../sections/rsvp/RsvpSection'),
      'event-details': () => import('../sections/details/EventDetailsSection'),
      venue: () => import('../sections/venue/VenueSection'),
      countdown: () => import('../sections/countdown/CountdownSection'),
      slideshow: () => import('../sections/slideshow/SlideshowSection'),
    };
    const loader = registry[section.type];
    if (loader) loader().then((mod) => setComponent(() => mod.default));
  }, [section.type]);
  if (!Component) return null;
  return (
    <Component
      section={section}
      data={data}
      theme={theme}
      settings={settings}
      rsvpRef={rsvpRef}
    />
  );
}

// ─── Main canvas ──────────────────────────────────────────────
export default function SectionV1CollageCanvas({
  section,
  data,
  theme,
  settings,
  rsvpRef,
  globalBgActive,
  inlineWidgets,
  isEditing = false,   // Motion 1.0 — Studio edit canvas passes true to keep motion off
}) {
  // Editor/public parity: in Studio (any viewport width) the editor
  // declares `layoutMode: 'mobile'` via CollageEditContext, so we read
  // `layout.mobile` exactly like a phone would. Without this override,
  // a 1920px Mac editor would read `layout.desktop` while the real
  // phone reads `layout.mobile` — when a template's element has
  // different mobile vs desktop coords (or width), the result is a
  // visible position/scale mismatch between Studio canvas and phone.
  // Public render has no provider, so falls back to viewport-width
  // detection.
  const editCtx = useContext(CollageEditContext);
  const viewportIsMobile = useIsMobile();
  const isMobile = editCtx?.layoutMode
    ? editCtx.layoutMode === 'mobile'
    : viewportIsMobile;
  const baseElements = data?._elements || section.elements || [];

  // Motion 1.0 — Soft Reveal applies only when explicitly enabled AND we're
  // not inside the Studio edit canvas. Each element with no explicit entrance
  // falls back to a role-tuned `soft-*` preset; the delay is grouped by
  // vertical band (0/0.08/0.16/0.24s) instead of per-index to avoid the
  // "stairstep" feel.
  const motionPreset = settings?.motionPreset || 'none';
  const softReveal = motionPreset === 'soft_reveal' && !isEditing;

  // Merge per-element overrides from invitation data (same shape as legacy).
  const mergedElements = baseElements.map((el) => {
    const overrides = data?.[el.id] || {};
    const mergedLayout = overrides.layout
      ? {
          ...el.layout,
          ...overrides.layout,
          desktop: { ...(el.layout?.desktop || {}), ...(overrides.layout?.desktop || {}) },
          mobile: { ...(el.layout?.mobile || {}), ...(overrides.layout?.mobile || {}) },
        }
      : el.layout;
    return { ...el, ...overrides, layout: mergedLayout };
  });

  const renderableElements = getRenderableElements(mergedElements, isMobile);
  const sectionLayout = resolveSectionLayout(section, data, isMobile);
  // CSS aspect-ratio is width/height. Our aspectRatio = height/width.
  const aspectCss = `1 / ${sectionLayout.aspectRatio}`;

  const sortedWidgets = (inlineWidgets || []).slice().sort((a, b) => a.yPosition - b.yPosition);

  return (
    <section
      id={section.id}
      className="relative w-full"
      style={{ aspectRatio: aspectCss }}
      data-section-type="collage"
      data-section-version="section-v1"
      data-section-height-px={sectionLayout.heightPx}
      data-section-height-source={sectionLayout.source}
      data-testid={`collage-section-v1-${section.id}`}
    >
      <div className="absolute inset-0 overflow-hidden">
        <BackgroundLayer background={section.background} globalBgActive={globalBgActive} />

        <div className="absolute inset-0">
          {renderableElements.map((element) => {
            const layoutEntry = pickV1LayoutForViewport(element.layout, isMobile);
            const bounds = resolveElementBounds(element, isMobile);
            const z = layoutEntry?.z || 1;
            const rotation = element.rotation || 0;
            const baseStyle = getElementV1Style(layoutEntry, z);
            const composedStyle = rotation
              ? { ...baseStyle, transform: `${baseStyle.transform} rotate(${rotation}deg)` }
              : baseStyle;

            return (
              <div
                key={element.id}
                style={composedStyle}
                data-testid={`v1-el-${element.id}`}
                data-section-id={section.id}
                data-element-id={element.id}
                data-element-type={element.type || (element.image_url ? 'image' : (element.text ? 'text' : 'element'))}
                data-element-name={element.name || element.text?.slice(0, 40) || undefined}
                data-bounds-source-x={bounds.sources.x}
                data-bounds-source-y={bounds.sources.y}
                data-bounds-source-w={bounds.sources.w}
                data-bounds-conflict={bounds.hasConflict ? 'true' : undefined}
                data-bounds-conflict-axes={bounds.hasConflict ? bounds.conflicts.map(c => c.axis).join(',') : undefined}
              >
                <ScrollEntrance
                  entrance={
                    element.entrance
                    || (softReveal ? softPresetForRole(getElementMotionRole(element)) : 'none')
                  }
                  delay={
                    element.entranceDelay
                    ?? (softReveal ? delayForGroup(getRevealGroup(element, isMobile)) : 0)
                  }
                >
                  <CollageElementRenderer
                    element={element}
                    isMobile={isMobile}
                    theme={theme}
                    rsvpRef={rsvpRef}
                    skipPositioning
                  />
                </ScrollEntrance>
              </div>
            );
          })}
        </div>
      </div>

      {/* Inline widgets — rendered at their Y position, NOT clipped by overflow */}
      {sortedWidgets.map((widget) => (
        <div
          key={widget.section.id}
          className="absolute left-0 right-0"
          style={{ top: `${widget.yPosition * 100}%`, zIndex: 50 }}
          data-testid={`v1-inline-widget-${widget.section.id}`}
        >
          <InlineWidgetRenderer
            section={widget.section}
            data={widget.data}
            theme={theme}
            settings={settings}
            rsvpRef={rsvpRef}
          />
        </div>
      ))}
    </section>
  );
}
